<template>
    <div
        class="flex flex-row items-center relative"
        :class="[roundedClasses, allocationHeightClass, columnWidthClass]"
        :style="[
            typeColorStyle,
            outerBackgroundColorStyle,
            outerBackgroundWidthStyle,
            innerBackgroundWidthStyle,
            stripeColorStyle,
            stripesBackgroundSizeStyle,
        ]"
    >
        <!-- Outer background -->
        <div
            class="absolute h-full w-(--outer-background-width)"
            :class="[outerBackgroundClasses, roundedClasses, borderClasses]"
        />

        <!-- Inner background -->
        <div
            v-if="props.variant === 'Inner-Strong-Mandatory' || props.variant === 'Inner-Strong-Optional'"
            class="w-(--inner-background-width) absolute"
            :class="[innerBackgroundClasses, allocationInnerContainerHeightClass]"
            :style="innerContainerStyle"
        />

        <!-- Specialization icon -->
        <div
            class="absolute text-(--color-allocation-part-text)"
            :class="{ 'opacity-40': props.variant === 'Faint-Mandatory' || props.variant === 'Faint-Optional' }"
            :style="iconPositionStyle"
        >
            <!-- Event icons inside -->
            <ClothesHangerIcon
                v-if="specializationIcon === 'ClothesHanger'"
                :class="[specializationIconSizeClass, 'drop-shadow-[0_0_0.1rem_var(--color-sky-800)]']"
            />
            <DoorBackIcon
                v-if="specializationIcon === 'DoorBack'"
                :class="[specializationIconSizeClass, 'drop-shadow-[0_0_0.1rem_var(--color-sky-800)]']"
            />
            <GroupIcon
                v-if="specializationIcon === 'Group'"
                :class="[specializationIconSizeClass, 'drop-shadow-[0_0_0.1rem_var(--color-sky-800)]']"
            />
            <InfoIcon
                v-if="specializationIcon === 'Info'"
                :class="[specializationIconSizeClass, 'drop-shadow-[0_0_0.1rem_var(--color-sky-800)]']"
            />
            <PackageIcon
                v-if="specializationIcon === 'Package'"
                :class="[specializationIconSizeClass, 'drop-shadow-[0_0_0.1rem_var(--color-sky-800)]']"
            />
            <ParkingIcon
                v-if="specializationIcon === 'Parking'"
                :class="[specializationIconSizeClass, 'drop-shadow-[0_0_0.1rem_var(--color-sky-800)]']"
            />
            <RestaurantIcon
                v-if="specializationIcon === 'Restaurant'"
                :class="[specializationIconSizeClass, 'drop-shadow-[0_0_0.1rem_var(--color-sky-800)]']"
            />
            <TruckIcon
                v-if="specializationIcon === 'Truck'"
                :class="[specializationIconSizeClass, 'drop-shadow-[0_0_0.1rem_var(--color-sky-800)]']"
            />
            <WalkingIcon
                v-if="specializationIcon === 'Walking'"
                :class="[specializationIconSizeClass, 'drop-shadow-[0_0_0.1rem_var(--color-sky-800)]']"
            />
            <PartyIcon
                v-if="specializationIcon === 'Party'"
                :class="[specializationIconSizeClass, 'drop-shadow-[0_0_0.1rem_var(--color-sky-800)]']"
            />

            <!-- Event icons outside -->
            <!-- Parking icon already above -->
            <CenterPointIcon
                v-if="specializationIcon === 'CenterPoint'"
                :class="[specializationIconSizeClass, 'drop-shadow-[0_0_0.1rem_var(--color-sky-800)]']"
            />
            <DirectionsRunIcon
                v-if="specializationIcon === 'DirectionsRun'"
                :class="[specializationIconSizeClass, 'drop-shadow-[0_0_0.1rem_var(--color-sky-800)]']"
            />
            <StoreFrontIcon
                v-if="specializationIcon === 'StoreFront'"
                :class="[specializationIconSizeClass, 'drop-shadow-[0_0_0.1rem_var(--color-sky-800)]']"
            />

            <!-- Project icons -->
            <EngineeringIcon
                v-if="specializationIcon === 'Engineering'"
                :class="[specializationIconSizeClass, 'drop-shadow-[0_0_0.1rem_var(--color-sky-800)]']"
            />
            <ConstructionIcon
                v-if="specializationIcon === 'Construction'"
                :class="[specializationIconSizeClass, 'drop-shadow-[0_0_0.1rem_var(--color-sky-800)]']"
            />
            <DangerousIcon
                v-if="specializationIcon === 'Dangerous'"
                :class="[specializationIconSizeClass, 'drop-shadow-[0_0_0.1rem_var(--color-sky-800)]']"
            />
        </div>

        <!-- Temporary change marker -->
        <div
            v-if="props.hasTemporaryChangeMarker"
            class="size-1.5 absolute bg-red-700 rounded-full shadow-[0_0_0.0625rem_0.0625rem] shadow-red-50/50"
            :style="temporaryChangeMarkerPositionStyle"
        />
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import CenterPointIcon from '../../Icon/CenterPointIcon.vue';
import ClothesHangerIcon from '../../Icon/ClothesHangerIcon.vue';
import ConstructionIcon from '../../Icon/ConstructionIcon.vue';
import DangerousIcon from '../../Icon/DangerousIcon.vue';
import DirectionsRunIcon from '../../Icon/DirectionsRunIcon.vue';
import DoorBackIcon from '../../Icon/DoorBackIcon.vue';
import EngineeringIcon from '../../Icon/EngineeringIcon.vue';
import GroupIcon from '../../Icon/GroupIcon.vue';
import InfoIcon from '../../Icon/InfoIcon.vue';
import PackageIcon from '../../Icon/PackageIcon.vue';
import ParkingIcon from '../../Icon/ParkingIcon.vue';
import PartyIcon from '../../Icon/PartyIcon.vue';
import RestaurantIcon from '../../Icon/RestaurantIcon.vue';
import StoreFrontIcon from '../../Icon/StoreFrontIcon.vue';
import TruckIcon from '../../Icon/TruckIcon.vue';
import WalkingIcon from '../../Icon/WalkingIcon.vue';
import { getBaseColorForPhaseType, getTextColorForPhaseType } from '../phaseColors';
import {
    allocationHeightClass,
    allocationHeightInRem,
    allocationInnerContainerHeightClass,
    allocationInnerContainerHeightInRem,
    columnWidthClass,
    columnWidthInRem,
    specializationIconSizeClass,
    specializationIconSizeInRem,
} from '../sharedStyles';
import { AllocationPartForm, PhaseColor } from '../Timeline.types';
import { mapSpecializationsTypesToIcons, SpecializationType } from './SpecializationMappings';

export type AllocationPartVariant =
    | 'Strong-Mandatory'
    | 'Strong-Optional'
    | 'Light-Mandatory'
    | 'Light-Optional'
    | 'Light-Red-Border-Mandatory'
    | 'Light-Red-Border-Optional'
    | 'Inner-Strong-Mandatory'
    | 'Inner-Strong-Optional'
    | 'Faint-Mandatory'
    | 'Faint-Optional';

interface AllocationPartProps {
    color: PhaseColor;
    form: AllocationPartForm;
    isPhaseEnd: boolean;
    variant: AllocationPartVariant;
    specialization: SpecializationType | null;
    hasTemporaryChangeMarker: boolean;
}

const props = defineProps<AllocationPartProps>();

const typeColorStyle = computed(() => {
    return {
        '--color-allocation-part': `var(${getBaseColorForPhaseType(props.color)})`,
        '--color-allocation-part-text': `var(${getTextColorForPhaseType(props.color)})`,
    };
});

const outerBackgroundColorStyle = computed(() => {
    const color100 = `var(--color-allocation-part)`;
    const color60 = `color-mix(in srgb, var(--color-allocation-part) 60%, white)`;
    const color40 = `color-mix(in srgb, var(--color-allocation-part) 40%, white)`;
    const color30 = `color-mix(in srgb, var(--color-allocation-part) 30%, white)`;

    switch (props.variant) {
        case 'Strong-Mandatory':
            return {
                '--outer-background-color': color100,
            };

        case 'Light-Mandatory':
            return {
                '--outer-background-color': color60,
            };

        case 'Faint-Mandatory':
            return {
                '--outer-background-color': color30,
            };

        case 'Light-Red-Border-Mandatory':
            return {
                '--outer-background-color': color60,
            };

        case 'Inner-Strong-Mandatory':
        case 'Inner-Strong-Optional':
            return {
                '--outer-background-color': color40,
            };
    }
});

const stripeColorStyle = computed(() => {
    const color100 = `var(--color-allocation-part)`;
    const color60 = `color-mix(in srgb, var(--color-allocation-part) 60%, white)`;
    const color40 = `color-mix(in srgb, var(--color-allocation-part) 40%, white)`;
    const color30 = `color-mix(in srgb, var(--color-allocation-part) 30%, white)`;
    const color20 = `color-mix(in srgb, var(--color-allocation-part) 20%, white)`;
    const color10 = `color-mix(in srgb, var(--color-allocation-part) 10%, white)`;

    switch (props.variant) {
        case 'Strong-Optional':
            return {
                '--color-stripe-strong-color': color100,
                '--color-stripe-light-color': color40,
            };

        case 'Light-Optional':
        case 'Light-Red-Border-Optional':
            return {
                '--color-stripe-strong-color': color60,
                '--color-stripe-light-color': color20,
            };

        case 'Inner-Strong-Optional':
            return {
                '--color-stripe-strong-color': color100,
                '--color-stripe-light-color': color40,
            };

        case 'Faint-Optional':
            return {
                '--color-stripe-strong-color': color30,
                '--color-stripe-light-color': color10,
            };
    }
});

const outerBackgroundClasses = computed(() => {
    switch (props.variant) {
        case 'Strong-Mandatory':
        case 'Light-Mandatory':
        case 'Light-Red-Border-Mandatory':
        case 'Inner-Strong-Mandatory':
        case 'Inner-Strong-Optional':
        case 'Faint-Mandatory':
            return 'bg-(--outer-background-color)';

        case 'Strong-Optional':
        case 'Light-Optional':
        case 'Light-Red-Border-Optional':
        case 'Faint-Optional':
            return 'stripes';
    }
});

const innerBackgroundClasses = computed(() => {
    switch (props.variant) {
        case 'Inner-Strong-Mandatory':
            return 'bg-(--color-allocation-part)';

        case 'Inner-Strong-Optional':
            return 'stripes';
    }
});

const outerBackgroundWidthStyle = computed(() => {
    if (props.isPhaseEnd || props.form === 'End' || props.form === 'Single') {
        return `--outer-background-width: ${columnWidthInRem}rem`;
    }

    return `--outer-background-width: ${columnWidthInRem + 0.0625}rem`;
});

const innerBackgroundWidthStyle = computed(() => {
    const spacingOutside = (allocationHeightInRem - allocationInnerContainerHeightInRem) / 2;

    if (props.variant === 'Inner-Strong-Mandatory' || props.variant === 'Inner-Strong-Optional') {
        if (props.form === 'Single') {
            return {
                '--inner-background-width': `${columnWidthInRem - spacingOutside * 2}rem`,
            };
        }

        if (props.form === 'Start' || props.form === 'End') {
            return {
                '--inner-background-width': `${columnWidthInRem - spacingOutside}rem`,
            };
        }

        if (props.isPhaseEnd) {
            return {
                '--inner-background-width': `${columnWidthInRem}rem`,
            };
        }

        return {
            '--inner-background-width': `${columnWidthInRem + 0.0625}rem`,
        };
    }
});

const borderClasses = computed(() => {
    if (props.variant === 'Light-Red-Border-Mandatory' || props.variant === 'Light-Red-Border-Optional') {
        switch (props.form) {
            case 'Start':
                return 'before:rounded-l-full border-inside border-inside-extended-width border-inside-left border-inside-top border-inside-bottom';

            case 'Middle':
                return 'border-inside border-inside-top border-inside-extended-width border-inside-bottom';

            case 'End':
                return 'before:rounded-r-full border-inside border-inside-normal-width border-inside-top border-inside-right border-inside-bottom';

            case 'Single':
                return 'before:rounded-full border-inside border-inside-normal-width border-inside-left border-inside-top border-inside-right border-inside-bottom';

            default:
                throw new Error(`Unknown form: ${props.form}`);
        }
    }
});

const roundedClasses = computed(() => {
    switch (props.form) {
        case 'Start':
            return 'rounded-l-full';

        case 'Middle':
            return '';

        case 'End':
            return 'rounded-r-full';

        case 'Single':
            return 'rounded-full';

        default:
            throw new Error(`Unknown form: ${props.form}`);
    }
});

const specializationIcon = computed(() => mapSpecializationsTypesToIcons(props.specialization));

const innerContainerStyle = computed(() => {
    if (props.form === 'Single') {
        return `
            margin-left: ${(allocationHeightInRem - allocationInnerContainerHeightInRem) / 2}rem;
            margin-right: ${(allocationHeightInRem - allocationInnerContainerHeightInRem) / 2}rem;
            border-top-left-radius: ${allocationInnerContainerHeightInRem / 2}rem;
            border-bottom-left-radius: ${allocationInnerContainerHeightInRem / 2}rem;
            border-top-right-radius: ${allocationInnerContainerHeightInRem / 2}rem;
            border-bottom-right-radius: ${allocationInnerContainerHeightInRem / 2}rem;
        `;
    }

    if (props.form === 'Start') {
        return `
            margin-left: ${(allocationHeightInRem - allocationInnerContainerHeightInRem) / 2}rem;
            border-top-left-radius: ${allocationInnerContainerHeightInRem / 2}rem;
            border-bottom-left-radius: ${allocationInnerContainerHeightInRem / 2}rem;
        `;
    }

    if (props.form === 'End') {
        return `
            margin-right: ${(allocationHeightInRem - allocationInnerContainerHeightInRem) / 2}rem;
            border-top-right-radius: ${allocationInnerContainerHeightInRem / 2}rem;
            border-bottom-right-radius: ${allocationInnerContainerHeightInRem / 2}rem;
        `;
    }
});

const iconPositionStyle = computed(() => {
    return `
        top: ${(allocationHeightInRem - specializationIconSizeInRem) / 2}rem;
        left: ${(columnWidthInRem - specializationIconSizeInRem) / 2}rem;
    `;
});

const temporaryChangeMarkerPositionStyle = computed(() => {
    // Top: Half height of temporary change marker
    // Right: Top-right corner of the icon box
    return `
        top: 0.1875rem;
        right: ${(columnWidthInRem - specializationIconSizeInRem) / 2 - 0.375 / 2}rem;
    `;
});

/**
 * Set as a custom property on the root rather than referenced from the <style> block with `v-bind()`.
 * A single `v-bind()` makes the compiler inject `useCssVars`, which runs a document-wide
 * `querySelectorAll('[data-v-owner=…]')` on mount and on every update and installs a MutationObserver on
 * the parent node — per instance. The timeline mounts over a thousand allocation parts, so that alone cost
 * ~160ms of scripting. Custom properties inherit, so `.stripes` on the child divs still picks this up,
 * exactly like `--color-stripe-strong-color` above.
 */
const stripesBackgroundSizeStyle = computed(() => {
    const size = (columnWidthInRem + 0.0625) / 2;

    return {
        '--stripes-background-size': `${size}rem ${size}rem`,
    };
});
</script>

<style scoped>
.border-inside::before {
    position: absolute;
    content: '';
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    height: 100%;
    z-index: 10;
}

.border-inside-normal-width::before {
    width: 100%;
}

.border-inside-extended-width::before {
    width: calc(100% + 1px);
}

.border-inside-left::before {
    border-left: 1px dashed var(--color-red-700);
}

.border-inside-top::before {
    border-top: 1px dashed var(--color-red-700);
}

.border-inside-right::before {
    border-right: 1px dashed var(--color-red-700);
}

.border-inside-bottom::before {
    border-bottom: 1px dashed var(--color-red-700);
}

.stripes {
    background-image: linear-gradient(
        45deg,
        var(--color-stripe-strong-color) 25%,
        var(--color-stripe-light-color) 25%,
        var(--color-stripe-light-color) 50%,
        var(--color-stripe-strong-color) 50%,
        var(--color-stripe-strong-color) 75%,
        var(--color-stripe-light-color) 75%,
        var(--color-stripe-light-color) 100%
    );
    background-size: var(--stripes-background-size);
}
</style>
